import React, {useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Colors, Input, Text} from '@optimaxdev/design-system';

import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {AnyAction} from 'redux';

import {TYP_CONTACT_LENSES} from 'constants/analytics/events/typ';
import {DOCTOR_DETAILS_DEFAULT} from 'constants/product';
import {CL_RX_METHOD} from 'constants/success';
import {getOrderDetails, getDrawerState} from 'desktop/pages/success/selectorsV2';
import {
    changeField,
    saveOrder,
    setErrorsSuccessPage,
    setIsAutocompleteModeEnabled,
} from 'desktop/pages/success/store/actions/orderItems/orderItems';
import {
    getDoctorDetailsValidated,
    getSuccessPageItem,
    getSuccessPageItems,
    getValidatedItems,
} from 'desktop/pages/success/store/selectors/orderItems/orderItems';
import {getCustomerFullName} from 'desktop/pages/success/store/selectors/orderLast/orderLast';
import {getOrderLoading} from 'desktop/pages/success/store/selectors/orderStatus/orderStatus';
import {ApplicationStoreType} from 'desktop/store';

import {getLabel, validateField} from 'libs/success/helpers/helpers';
import {getPopupType} from 'libs/success/prescriptionPopup/prescriptionPopup';
import {DoctorDetailsType, SUCCESS_PAGE_FIELDS_DEFAULT} from 'types/successPage';

import {AutoCompleteWrapper} from '../autoCompleteWrapper/autoCompleteWrapper';
import {ScreenWrapper} from '../screenWrapper/screenWrapper';

import {Button} from './helpers';

/**
 * Container for DOCTOR screen of cl typ
 */
export const SmartDoctor = () => {
    const {
        customerFullName,
        id,
        isLoading,
        item,
        items,
        successPageItem,
        successPageItemsValidated,
        validatedItems,
    } = useSelector(
        (store: ApplicationStoreType) => ({
            customerFullName: getCustomerFullName(store),
            id: getOrderDetails(store).id,
            isLoading: getOrderLoading(store),
            item: getDrawerState(store).item,
            items: getSuccessPageItems(store),
            successPageItem: getSuccessPageItem(store),
            successPageItemsValidated: getDoctorDetailsValidated(store),
            validatedItems: getValidatedItems(store),
        }),
        shallowEqual,
    );
    const dispatch = useDispatch();
    const parsedItem = validatedItems?.[item?.id || ''] as DoctorDetailsType;

    const resetError = (key: string) => {
        dispatch(
            setErrorsSuccessPage({
                [String(item?.id)]: {
                    ...DOCTOR_DETAILS_DEFAULT,
                    ...parsedItem,
                    [key]: '',
                },
            }),
        );
    };

    const handleChangeField = (key: string, value: string) => {
        dispatch(
            changeField({
                ...SUCCESS_PAGE_FIELDS_DEFAULT,
                doctorDetails: {
                    ...(successPageItem?.doctorDetails as DoctorDetailsType),
                    [key]: value,
                },
                fileType: '',
                orderItemId: String(item?.id),
                sendBy: item?.prescription?.send_by || 0,
                type: 'success',
            }),
        );
    };
    const type = getPopupType(item, '');

    useEffect(() => {
        dispatch(
            setIsAutocompleteModeEnabled({
                isAutocompleteModeEnabled: true,
                orderItemId: String(item?.id),
            }),
        );
        if (customerFullName) {
            handleChangeField('patientName', customerFullName);
        }
    }, []);

    const handleUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {target} = event;
        handleChangeField(target.name, target.value);
        resetError(target.name);
    };

    const handleSubmit = () => {
        const validation = successPageItemsValidated;
        sendDesktopAnalyticsEvent(TYP_CONTACT_LENSES, getLabel(CL_RX_METHOD.DOCTOR, true, type));

        if (validation.isAllValid) {
            const action = saveOrder({itemId: String(item?.id), items, orderId: id});
            dispatch(action as unknown as AnyAction);
            return;
        }
        sendDesktopAnalyticsEvent(
            TYP_CONTACT_LENSES,
            `${getLabel(CL_RX_METHOD.DOCTOR, false, type)} - Error-Invalid field`,
        );
        dispatch(setErrorsSuccessPage(validation.items));
    };

    return (
        <ScreenWrapper buttons={<Button isLoading={isLoading} onClick={handleSubmit} />}>
            <Text
                as="p"
                color={Colors.limedSpruce}
                lineHeight="24px"
                margin="0 0 2px 0"
                size="16px"
                bold
            >
                Enter Your eye doctor/clinic details
            </Text>
            <Text as="p" color={Colors.limedSpruce} lineHeight="24px" size="16px">
                We’ll quickly verify your prescription with your doctor to ensure you will get the
                exact contact lenses you need.
            </Text>
            <Text as="p" color={Colors.nevada} lineHeight="150%" margin="13px 0 8px" size="14px">
                This prescription is for:
            </Text>
            <Input
                isSuccess={Boolean(
                    validateField('patientName', successPageItem?.doctorDetails?.patientName || ''),
                )}
                ariaLabel="Patient Name"
                id="patientName"
                isError={Boolean(parsedItem?.patientName)}
                label="Patient Name"
                message={parsedItem?.patientName}
                name="patientName"
                onChange={handleUpdate}
                size="large"
                value={successPageItem?.doctorDetails?.patientName}
            />

            <AutoCompleteWrapper resetError={resetError} />
        </ScreenWrapper>
    );
};
